import * as THREE from 'three';

/**
 * Marks on the floor for the omens, one instanced quad each: a lightning telegraph (a crackling ring that
 * closes and fills as the bolt comes), a falling star's target (a pale ring with rays drawn inward), a
 * scorch left by a bolt, and a star's crater that glows and cools. Shape, fill and fade live in instance
 * attributes; telegraphs are refilled every frame, lasting marks are put down once and age on a clock.
 */

export const MARK = { bolt: 0, starTarget: 1, scorch: 2, crater: 3 } as const;

const VERT = /* glsl */ `
attribute vec4 aMark; // kind, fill (0..1) or birth (s), seed, life (s; 0 = live telegraph)
uniform float uTime;
varying vec2 vUv;
varying vec4 vMark;
varying float vAge;
void main() {
  vUv = uv * 2.0 - 1.0;
  vMark = aMark;
  vAge = aMark.w > 0.0 ? (uTime - aMark.y) / aMark.w : -1.0;
  gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
}`;

const FRAG = /* glsl */ `
uniform float uTime;
varying vec2 vUv;
varying vec4 vMark;
varying float vAge;
float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1, 0)), u.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), u.x), u.y);
}
void main() {
  float r = length(vUv);
  float a = atan(vUv.y, vUv.x);
  float kind = vMark.x;
  float seed = vMark.z;
  vec3 col;
  float alpha;
  if (kind < 0.5) {
    // Lightning to come: a crackling white-blue rim, an inner ring closing on the center as it fills, a flicker that quickens.
    float fill = vMark.y;
    float crack = noise(vec2(a * 6.0 + seed * 10.0, uTime * 18.0)) * 0.06;
    float rim = smoothstep(0.06, 0.0, abs(r - 0.94 + crack));
    float inner = 1.0 - fill;
    float closing = smoothstep(0.05, 0.0, abs(r - inner * 0.94));
    float disc = (1.0 - smoothstep(inner * 0.94 - 0.02, inner * 0.94, r)) * 0.0 + step(r, 0.94) * (0.14 + 0.3 * fill * fill);
    // Forks of light running out from the center.
    float forks = pow(max(0.0, sin(a * 5.0 + seed * 30.0 + noise(vec2(r * 8.0, seed)) * 2.0)), 40.0) * step(r, 0.94) * fill;
    float flick = 0.75 + 0.25 * sin(uTime * (10.0 + 30.0 * fill));
    col = mix(vec3(0.45, 0.65, 1.0), vec3(0.92, 0.97, 1.0), fill);
    alpha = (rim * 0.95 + closing * 0.8 + disc + forks * 0.8) * flick;
  } else if (kind < 1.5) {
    // A star to come: a pale silver ring, rays drawn toward the center, brighter as it nears.
    float fill = vMark.y;
    float rim = smoothstep(0.05, 0.0, abs(r - 0.92));
    float rays = pow(max(0.0, cos(a * 6.0 + uTime * 0.6)), 24.0) * smoothstep(0.92, 0.2, r) * smoothstep(0.0, 0.3, r) * (0.3 + fill);
    float core = (1.0 - smoothstep(0.0, 0.35 + 0.2 * fill, r)) * fill;
    float closing = smoothstep(0.04, 0.0, abs(r - (1.0 - fill) * 0.92));
    col = vec3(0.78, 0.86, 1.0);
    alpha = rim * 0.7 + rays * 0.6 + core * 0.7 + closing * 0.5 + step(r, 0.92) * 0.08;
  } else if (kind < 2.5) {
    // A scorch: black-brown char with a jagged edge and cracks that glow a moment, then only the char.
    float edge = 0.7 + 0.12 * noise(vec2(a * 3.0 + seed * 7.0, seed)) + 0.08 * sin(a * 7.0 + seed * 11.0);
    float inside = 1.0 - smoothstep(edge - 0.15, edge, r);
    float cracks = pow(max(0.0, sin(a * 9.0 + seed * 20.0 + r * 4.0)), 30.0) * step(r, edge) * smoothstep(0.0, 0.15, r);
    float hot = max(0.0, 1.0 - vAge * 8.0);
    col = mix(vec3(0.04, 0.035, 0.04), vec3(0.55, 0.8, 1.0), cracks * hot);
    alpha = inside * 0.75 + cracks * hot * 0.8;
    alpha *= 1.0 - smoothstep(0.7, 1.0, vAge);
  } else {
    // A star's crater: a scorched ring of light around a white-hot center that cools to silver, then fades.
    float edge = 0.82 + 0.06 * sin(a * 5.0 + seed * 9.0);
    float bowl = 1.0 - smoothstep(edge - 0.2, edge, r);
    float lip = smoothstep(0.1, 0.0, abs(r - edge + 0.08));
    float hot = max(0.0, 1.0 - vAge * 3.0);
    float glints = step(0.985, hash(floor(vUv * 18.0) + seed)) * step(r, edge) * (0.5 + 0.5 * sin(uTime * 4.0 + seed * 50.0 + vUv.x * 20.0));
    col = mix(vec3(0.08, 0.09, 0.14), vec3(0.85, 0.92, 1.0), hot * (1.0 - smoothstep(0.0, 0.6, r)) + lip * 0.4 + glints);
    alpha = bowl * 0.6 + lip * 0.5 * (0.3 + hot) + glints * 0.8;
    alpha *= 1.0 - smoothstep(0.6, 1.0, vAge);
  }
  if (vAge > 1.0 || alpha <= 0.01) discard;
  gl_FragColor = vec4(col, min(1.0, alpha));
  #include <colorspace_fragment>
}`;

export class GroundMarks {
  readonly group = new THREE.Group();
  private lasting: THREE.InstancedMesh;
  private live: THREE.InstancedMesh;
  private lastingMark: THREE.InstancedBufferAttribute;
  private liveMark: THREE.InstancedBufferAttribute;
  private uniforms = { uTime: { value: 0 } };
  private next = 0;
  private n = 0;
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private v = new THREE.Vector3();
  private sc = new THREE.Vector3();
  private now = 0;

  constructor(lasting = 48, live = 24) {
    const mk = (size: number, blending: THREE.Blending, order: number): [THREE.InstancedMesh, THREE.InstancedBufferAttribute] => {
      const geo = new THREE.PlaneGeometry(2, 2).rotateX(-Math.PI / 2);
      const attr = new THREE.InstancedBufferAttribute(new Float32Array(size * 4), 4).setUsage(THREE.DynamicDrawUsage);
      geo.setAttribute('aMark', attr);
      const mat = new THREE.ShaderMaterial({ uniforms: this.uniforms, vertexShader: VERT, fragmentShader: FRAG, transparent: true, depthWrite: false, blending, polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -8 });
      const mesh = new THREE.InstancedMesh(geo, mat, size);
      mesh.frustumCulled = false;
      mesh.renderOrder = order;
      mesh.count = 0;
      return [mesh, attr];
    };
    [this.lasting, this.lastingMark] = mk(lasting, THREE.NormalBlending, 1);
    [this.live, this.liveMark] = mk(live, THREE.AdditiveBlending, 2);
    this.group.add(this.lasting, this.live);
  }

  /** A lasting mark (scorch, crater), world point, radius, seconds. */
  put(kind: number, p: THREE.Vector3, r: number, life: number, seed: number): void {
    const i = this.next;
    const size = this.lasting.instanceMatrix.count;
    this.next = (this.next + 1) % size;
    this.q.setFromAxisAngle(THREE.Object3D.DEFAULT_UP, seed * 6.283);
    this.lasting.setMatrixAt(i, this.m.compose(this.v.copy(p).setY(p.y + 0.035), this.q, this.sc.set(r, 1, r)));
    this.lastingMark.setXYZW(i, kind, this.now, seed, life);
    this.lasting.count = Math.max(this.lasting.count, i + 1);
    this.lasting.instanceMatrix.needsUpdate = true;
    this.lastingMark.needsUpdate = true;
  }

  /** Telegraphs drawn this frame: call begin, add each, end. */
  begin(): void {
    this.n = 0;
  }

  add(kind: number, p: THREE.Vector3, r: number, fill: number, seed: number): void {
    if (this.n >= this.live.instanceMatrix.count) return;
    const i = this.n++;
    this.q.identity();
    this.live.setMatrixAt(i, this.m.compose(this.v.copy(p).setY(p.y + 0.045), this.q, this.sc.set(r, 1, r)));
    this.liveMark.setXYZW(i, kind, fill, seed, 0);
  }

  end(): void {
    this.live.count = this.n;
    this.live.instanceMatrix.needsUpdate = true;
    this.liveMark.needsUpdate = true;
  }

  frame(t: number): void {
    this.now = t;
    this.uniforms.uTime.value = t;
  }

  dispose(): void {
    for (const m of [this.lasting, this.live]) {
      m.geometry.dispose();
      (m.material as THREE.Material).dispose();
    }
  }
}
